import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Dimmer, Loader, Segment } from "semantic-ui-react";
import AcceptanceStatusStackBar from "../../common/ui/acceptance-status-stack-bar/AcceptanceStatusStackBar";
import SelectFilter from "../../common/ui/select-filter/SelectFilter";
import { buildAcceptanceBarSegments } from "../../common/utils/acceptance/buildAcceptanceBarSegments";
import "./Documents.css";
import { axiosBasic } from "../../../api/axios";
import { config } from "../../../api/endPoints";
import { Common, WebStorage, WebStorageNames } from "../../../utils";

const PAGE_SIZE = 20;

/** Client limit (DB may allow more); keep comments concise in the UI. */
const ACK_COMMENT_MAX_LENGTH = 300;

const getUserRoleId = () => {
  try {
    const r = WebStorage.getLocalStore(WebStorageNames.RoleId);
    return r != null && r !== "" ? parseInt(String(r), 10) : null;
  } catch {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem("roleId") : null;
    return raw != null && raw !== "" ? parseInt(String(raw), 10) : null;
  }
};

/** Manager (3) and Standard User (4) — not company/office admins (1, 2). */
const isManagerOrStandardUserRole = (roleId) => roleId === 3 || roleId === 4;

/** Same wireframe cube as office dashboard header — white on purple */
const DocumentPortalCubeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} fill="white" viewBox="0 0 16 16" aria-hidden>
    <path d="M8.186 1.113a.5.5 0 0 0-.372 0L1.846 3.5 8 5.961 14.154 3.5 8.186 1.113zM15 4.239l-6.5 2.6v7.922l6.5-2.6V4.24zM7.5 14.762V6.838L1 4.239v7.923l6.5 2.6zM7.443.184a1.5 1.5 0 0 1 1.114 0l7.129 2.852A.5.5 0 0 1 16 3.5v8.662a1 1 0 0 1-.629.928l-7.185 2.874a.5.5 0 0 1-.372 0L.63 13.09a1 1 0 0 1-.63-.928V3.5a.5.5 0 0 1 .314-.464L7.443.184z" />
  </svg>
);

const categoryLabels = {
  InternalPolicy: "Internal Policy",
  Regulatory: "Regulatory",
  Training: "Training",
  TermsAndConditions: "Terms & Conditions",
  EmployeeHandbook: "Employee Handbook",
  Safety: "Safety",
  Other: "Other",
};

const CATEGORY_INDEX_TO_KEY = [
  "InternalPolicy",
  "Regulatory",
  "Training",
  "TermsAndConditions",
  "EmployeeHandbook",
  "Safety",
  "Other",
];

const getCategoryLabel = (category) => {
  if (category === null || category === undefined) return "—";
  if (typeof category === "string") return categoryLabels[category] || category;
  if (typeof category === "number") {
    const key = CATEGORY_INDEX_TO_KEY[category];
    return key ? categoryLabels[key] || key : String(category);
  }
  return String(category);
};

const coercePriority = (v) => {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const map = { Low: 0, Medium: 1, High: 2, Critical: 3, FollowUp: 4 };
  if (typeof v === "string" && map[v] !== undefined) return map[v];
  return 1;
};

/** Backend CompanyDocumentOfficeScope */
const CompanyDocumentOfficeScope = {
  AllOffices: 0,
  SpecificOffices: 1,
};

const parseApplicableOffices = (doc) => {
  const raw = doc?.applicableOffices ?? doc?.ApplicableOffices ?? null;
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [];
  return arr
    .map((x) => {
      if (!x || typeof x !== "object") return null;
      const idRaw = x.id ?? x.officeId ?? x.OfficeId;
      const nameRaw = x.name ?? x.officeName ?? x.Name ?? "";
      const name = String(nameRaw).trim();
      if (idRaw === undefined || idRaw === null) return null;
      const id =
        typeof idRaw === "number"
          ? idRaw
          : typeof idRaw === "string" && /^\d+$/.test(idRaw.trim())
            ? Number(idRaw.trim())
            : idRaw;
      return { id, name: name || `Office ${idRaw}` };
    })
    .filter(Boolean);
};

const mapEmployeeView = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const rawScope = raw.officeScope ?? raw.OfficeScope;
  let officeScope = CompanyDocumentOfficeScope.AllOffices;
  if (typeof rawScope === "number" && !Number.isNaN(rawScope)) {
    officeScope = rawScope;
  } else if (typeof rawScope === "string") {
    const n = Number(rawScope);
    if (!Number.isNaN(n)) officeScope = n;
    else if (rawScope === "SpecificOffices") officeScope = CompanyDocumentOfficeScope.SpecificOffices;
  }

  const merged = { ...raw, officeScope, applicableOffices: parseApplicableOffices(raw) };

  return {
    id: String(raw.id ?? raw.Id ?? ""),
    title: raw.title ?? raw.Title ?? "—",
    description: raw.description ?? raw.Description ?? "",
    category: raw.category ?? raw.Category,
    priority: coercePriority(raw.priority ?? raw.Priority),
    fileName: raw.fileName ?? raw.FileName ?? "",
    fileSize: Number(raw.fileSize ?? raw.FileSize) || 0,
    fileType: raw.fileType ?? raw.FileType ?? "",
    requiresAcceptance: raw.requiresAcceptance ?? raw.RequiresAcceptance ?? true,
    allowRejection: raw.allowRejection ?? raw.AllowRejection ?? true,
    officeScope,
    applicableOffices: merged.applicableOffices,
    createdDate: raw.createdDate ?? raw.CreatedDate ?? null,
  };
};

/**
 * Resolves acknowledgement row id from API shapes (camelCase / PascalCase / C# `Id` on list item).
 * Does not use nested `document.id` — that is the document id, not the acknowledgement id.
 */
const extractAcknowledgementId = (raw) => {
  if (!raw || typeof raw !== "object") return "";
  const nested = raw.document ?? raw.Document;
  const nestedDocId =
    nested != null ? String(nested.id ?? nested.Id ?? nested.documentId ?? nested.DocumentId ?? "").trim() : "";

  const tryStr = (v) => {
    if (v === undefined || v === null) return "";
    const s = String(v).trim();
    return s === "" || s === "undefined" || s === "null" ? "" : s;
  };

  const direct =
    tryStr(raw.acknowledgementId) ||
    tryStr(raw.AcknowledgementId) ||
    (nested
      ? tryStr(nested.acknowledgementId) || tryStr(nested.AcknowledgementId)
      : "");

  if (direct) return direct;

  const rootId = tryStr(raw.id) || tryStr(raw.Id);
  if (!rootId) return "";

  if (nestedDocId && rootId === nestedDocId) {
    return "";
  }

  return rootId;
};

/** Backend CompanyDocumentAcceptanceListItem — nested Document + optional file preview */
const mapAcceptanceListItem = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  const nested = raw.document ?? raw.Document;
  if (nested) {
    const doc = mapEmployeeView(nested);
    if (!doc) return null;
    const acknowledgementId = extractAcknowledgementId(raw);
    return {
      ...doc,
      acknowledgementId,
      fileContentBase64: raw.fileContentBase64 ?? raw.FileContentBase64 ?? "",
      contentType: raw.contentType ?? raw.ContentType ?? "application/octet-stream",
      filePreviewNote: raw.filePreviewNote ?? raw.FilePreviewNote ?? "",
      source: "required",
    };
  }
  const flat = mapEmployeeView(raw);
  if (!flat) return null;
  return {
    ...flat,
    acknowledgementId: extractAcknowledgementId(raw),
    fileContentBase64: raw.fileContentBase64 ?? raw.FileContentBase64 ?? "",
    contentType: raw.contentType ?? raw.ContentType ?? "application/octet-stream",
    filePreviewNote: raw.filePreviewNote ?? raw.FilePreviewNote ?? "",
    source: "required",
  };
};

const mapPublishedListItem = (raw) => {
  const base = mapAcceptanceListItem(raw);
  if (!base) return null;
  const ms = raw.myAcknowledgementStatus ?? raw.MyAcknowledgementStatus;
  const responded = raw.respondedDate ?? raw.RespondedDate ?? null;
  const pf = raw.isPendingActionItem ?? raw.IsPendingActionItem;
  return {
    ...base,
    myAcknowledgementStatus: coerceAckStatus(ms),
    respondedDate: responded,
    source: "published",
    isPendingActionItem: typeof pf === "boolean" ? pf : undefined,
  };
};

/** Rows from GetDocumentsForAcceptance that belong in the published queue only (not Required Actions). */
const rawItemIsPublishedPendingActionOnly = (raw) => {
  if (!raw || typeof raw !== "object") return false;
  if (raw.isPendingActionItem === true || raw.IsPendingActionItem === true) return true;
  if (raw.pendingActionItem === true || raw.PendingActionItem === true) return true;
  const nested = raw.document ?? raw.Document;
  if (nested && (nested.isPendingActionItem === true || nested.IsPendingActionItem === true)) return true;
  const kind = raw.queueKind ?? raw.QueueKind ?? raw.itemKind ?? raw.ItemKind;
  if (typeof kind === "string" && kind.toLowerCase().replace(/\s/g, "") === "pendingaction") return true;
  return false;
};

const ackStatusLabel = (status) => {
  if (status === MY_ACK_STATUS.Accepted) return "Accepted";
  if (status === MY_ACK_STATUS.Rejected) return "Rejected";
  return "Pending";
};

const ackStatusChipClass = (status) => {
  if (status === MY_ACK_STATUS.Accepted) return "documents-published-status documents-published-status--accepted";
  if (status === MY_ACK_STATUS.Rejected) return "documents-published-status documents-published-status--rejected";
  return "documents-published-status documents-published-status--pending";
};

const tileActionLabel = (doc) => {
  if (doc.source === "required") return "Review & Acknowledge";
  if (!doc.requiresAcceptance) return "View";
  if (doc.myAcknowledgementStatus === MY_ACK_STATUS.Pending) return "Review & Acknowledge";
  return "View";
};

const formatRespondedDateTime = (iso) => {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("MMM D, YYYY h:mm A") : "—";
};

const normalizeDocumentsList = (data) => {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.documents)) return data.documents;
  if (Array.isArray(data.acceptanceItems)) return data.acceptanceItems;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

/** Matches backend CompanyDocumentAcceptanceStatus for list rows */
const MY_ACK_STATUS = {
  Pending: 0,
  Accepted: 1,
  Rejected: 2,
};

const coerceAckStatus = (v) => {
  if (v === null || v === undefined) return MY_ACK_STATUS.Pending;
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  const s = String(v).toLowerCase();
  if (s === "pending" || s === "0") return MY_ACK_STATUS.Pending;
  if (s === "accepted" || s === "1") return MY_ACK_STATUS.Accepted;
  if (s === "rejected" || s === "2") return MY_ACK_STATUS.Rejected;
  return MY_ACK_STATUS.Pending;
};

const extractPagedItemsAndTotal = (body) => {
  const data = body?.data ?? body?.Data ?? body;
  if (data == null) return { items: [], total: undefined };
  if (Array.isArray(data)) {
    const total =
      body?.totalCount ??
      body?.TotalCount ??
      body?.total ??
      body?.Total;
    const n =
      typeof total === "number" && !Number.isNaN(total)
        ? total
        : total != null
          ? Number(total)
          : undefined;
    return { items: data, total: Number.isNaN(n) ? undefined : n };
  }
  const items = normalizeDocumentsList(data);
  const total =
    data?.totalCount ??
    data?.TotalCount ??
    data?.total ??
    data?.Total ??
    body?.totalCount ??
    body?.TotalCount ??
    body?.total ??
    body?.Total;
  const n =
    typeof total === "number" && !Number.isNaN(total)
      ? total
      : total != null
        ? Number(total)
        : undefined;
  return { items, total: Number.isNaN(n) ? undefined : n };
};

/** Same mapping as GET GetDocumentsForAcceptance — used for POST Respond paged payload */
const buildRequiredDocumentsFromAcceptanceResponse = (body) => {
  const { items: rawItems, total } = extractPagedItemsAndTotal(body);
  const mapped = rawItems
    .filter((r) => !rawItemIsPublishedPendingActionOnly(r))
    .map(mapAcceptanceListItem)
    .filter(Boolean);
  return { mapped, total };
};

const clampAckPercent = (n) => {
  const x = Number(n);
  if (Number.isNaN(x)) return 0;
  return Math.min(100, Math.max(0, Math.round(x)));
};

/** CompanyDocumentPortalAcceptanceStats — portal list + Respond payload */
const normalizePortalAcceptanceStats = (raw) => {
  if (!raw || typeof raw !== "object") return null;
  return {
    totalRequiringAcceptance: Number(raw.totalRequiringAcceptance ?? raw.TotalRequiringAcceptance) || 0,
    acceptedCount: Number(raw.acceptedCount ?? raw.AcceptedCount) || 0,
    pendingCount: Number(raw.pendingCount ?? raw.PendingCount) || 0,
    rejectedCount: Number(raw.rejectedCount ?? raw.RejectedCount) || 0,
    documentsNotRequiringAcceptance:
      Number(raw.documentsNotRequiringAcceptance ?? raw.DocumentsNotRequiringAcceptance) || 0,
    acceptancePercent: clampAckPercent(raw.acceptancePercent ?? raw.AcceptancePercent),
  };
};

const extractAcceptanceStatsFromPortalBody = (body) => {
  const data = body?.data ?? body?.Data ?? body;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const raw = data.acceptanceStats ?? data.AcceptanceStats;
  return normalizePortalAcceptanceStats(raw);
};

/** POST Respond — CompanyDocumentAcceptanceStatus */
const API_ACCEPTANCE_STATUS = {
  Accepted: 1,
  Rejected: 2,
};

const buildBlobUrlFromBase64 = (base64, contentType) => {
  if (!base64 || typeof base64 !== "string") return null;
  try {
    const clean = base64.replace(/\s/g, "");
    const binary = atob(clean);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i += 1) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: contentType || "application/octet-stream" });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
};

const isBrowserPreviewableMime = (mime) => {
  if (!mime || typeof mime !== "string") return false;
  const m = mime.toLowerCase();
  return (
    m.includes("pdf") ||
    m.startsWith("image/") ||
    m.startsWith("text/plain") ||
    m.includes("text/html")
  );
};

const formatFileSize = (bytes) => {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const formatCreatedDate = (iso) => {
  if (!iso) return "—";
  const m = moment(iso);
  return m.isValid() ? m.format("MMM D, YYYY") : "—";
};

/** Human-readable type — API often sends raw MIME (e.g. application/pdf). */
const formatFileTypeLabel = (mime, fileName) => {
  const m = (mime || "").toLowerCase().trim();
  if (m.includes("pdf")) return "PDF";
  if (m.includes("wordprocessingml") || m.includes("msword")) return "Word";
  if (m.includes("spreadsheetml") || m === "application/vnd.ms-excel") return "Excel";
  if (m.includes("presentationml") || m.includes("powerpoint")) return "PowerPoint";
  if (m.includes("plain")) return "Text";
  if (m.includes("html")) return "HTML";
  if (m.startsWith("image/")) return "Image";
  if (fileName && fileName.includes(".")) {
    const ext = fileName.split(".").pop();
    if (ext && /^[a-z0-9]+$/i.test(ext) && ext.length <= 8) return ext.toUpperCase();
  }
  if (m && m.includes("/")) {
    const part = m.split("/").pop();
    if (part && part.length <= 24) return part.length > 14 ? `${part.slice(0, 12)}…` : part;
  }
  return m ? "File" : "—";
};

const officeScopeLabel = (doc) => {
  if (doc.officeScope === CompanyDocumentOfficeScope.AllOffices) return "All offices";
  const names = (doc.applicableOffices || []).map((o) => o.name).filter(Boolean);
  if (names.length === 0) return "Specific offices";
  return names.join(", ");
};

/** High / Critical → URGENT-style badge on cards */
const isUrgentPriority = (priority) => priority === 2 || priority === 3;

const categoryIconClass = (category) => {
  const key =
    typeof category === "number" ? CATEGORY_INDEX_TO_KEY[category] : category;
  const k = key || "";
  if (k === "Regulatory" || k === "Safety") return "bi-shield-check";
  if (k === "Training") return "bi-mortarboard";
  if (k === "TermsAndConditions" || k === "EmployeeHandbook") return "bi-journal-text";
  if (k === "InternalPolicy") return "bi-building";
  return "bi-file-earmark-text";
};

const RequiredActions = () => {
  const userRoleId = getUserRoleId();
  const showPublishedCompanyTab = isManagerOrStandardUserRole(userRoleId);

  const [portalTab, setPortalTab] = useState(() =>
    isManagerOrStandardUserRole(getUserRoleId()) ? "published" : "required"
  );

  const [documents, setDocuments] = useState([]);
  const requiredPageRef = useRef(1);
  const [requiredTotal, setRequiredTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMoreRequired, setLoadingMoreRequired] = useState(false);

  const [publishedDocs, setPublishedDocs] = useState([]);
  const publishedPageRef = useRef(1);
  const [publishedTotal, setPublishedTotal] = useState(null);
  const [publishedLoading, setPublishedLoading] = useState(true);
  const [publishedLoadingMore, setPublishedLoadingMore] = useState(false);
  const [offices, setOffices] = useState([]);
  const [filterOfficeId, setFilterOfficeId] = useState(null);
  /** From GetDocumentsForAcceptance / GetPublishedCompanyDocuments / Respond — portal acceptance stats */
  const [portalAcceptanceStats, setPortalAcceptanceStats] = useState(null);

  const portalAckBarSegments = useMemo(
    () => buildAcceptanceBarSegments(portalAcceptanceStats),
    [portalAcceptanceStats]
  );

  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [response, setResponse] = useState("");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadRequired = useCallback(async (append) => {
    if (append) setLoadingMoreRequired(true);
    else setLoading(true);
    try {
      const nextPage = append ? requiredPageRef.current + 1 : 1;
      const { CompanyDocuments } = config.REST_API;
      const res = await axiosBasic.get(CompanyDocuments.GetDocumentsForAcceptance, {
        params: { page: nextPage, pageSize: PAGE_SIZE },
      });
      const body = res.data || {};
      const { success, message } = body;
      if (!success) {
        Common.showErrorMessage(message || "Could not load documents.");
        if (!append) {
          setDocuments([]);
          setRequiredTotal(0);
        }
        return;
      }
      const requiredStats = extractAcceptanceStatsFromPortalBody(body);
      if (requiredStats) {
        setPortalAcceptanceStats(requiredStats);
      }
      const { mapped, total } = buildRequiredDocumentsFromAcceptanceResponse(body);
      if (append) {
        setDocuments((prev) => [...prev, ...mapped]);
        requiredPageRef.current = nextPage;
      } else {
        setDocuments(mapped);
        requiredPageRef.current = 1;
      }
      if (typeof total === "number" && !Number.isNaN(total)) {
        setRequiredTotal(total);
      } else if (!append) {
        setRequiredTotal(mapped.length);
      }
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Could not load documents.");
      if (!append) {
        setDocuments([]);
        setRequiredTotal(0);
      }
    } finally {
      setLoading(false);
      setLoadingMoreRequired(false);
    }
  }, []);

  const loadPublished = useCallback(async (append) => {
    if (append) setPublishedLoadingMore(true);
    else setPublishedLoading(true);
    try {
      const nextPage = append ? publishedPageRef.current + 1 : 1;
      const { CompanyDocuments } = config.REST_API;
      const params = { page: nextPage, pageSize: PAGE_SIZE };
      if (filterOfficeId != null) params.officeId = filterOfficeId;
      const res = await axiosBasic.get(CompanyDocuments.GetPublishedCompanyDocuments, { params });
      const body = res.data || {};
      const { success, message } = body;
      if (!success) {
        Common.showErrorMessage(message || "Could not load published documents.");
        if (!append) {
          setPublishedDocs([]);
          setPublishedTotal(0);
        }
        return;
      }
      const stats = extractAcceptanceStatsFromPortalBody(body);
      if (stats) {
        setPortalAcceptanceStats(stats);
      }
      const { items: rawItems, total } = extractPagedItemsAndTotal(body);
      const mapped = rawItems.map(mapPublishedListItem).filter(Boolean);
      if (append) {
        setPublishedDocs((prev) => [...prev, ...mapped]);
        publishedPageRef.current = nextPage;
      } else {
        setPublishedDocs(mapped);
        publishedPageRef.current = 1;
      }
      if (typeof total === "number" && !Number.isNaN(total)) {
        setPublishedTotal(total);
      } else if (!append) {
        setPublishedTotal(mapped.length);
      }
    } catch (err) {
      Common.showErrorMessage(
        err?.response?.data?.message || err?.message || "Could not load published documents."
      );
      if (!append) {
        setPublishedDocs([]);
        setPublishedTotal(0);
      }
    } finally {
      setPublishedLoading(false);
      setPublishedLoadingMore(false);
    }
  }, [filterOfficeId]);

  /** Acknowledgements that appear as pending “action” items under published docs — hide duplicates from Required Actions. */
  const publishedAckIdsToHideFromRequired = useMemo(() => {
    if (!publishedDocs.length) return new Set();
    const hasExplicitPendingActionFlag = publishedDocs.some(
      (d) => d.isPendingActionItem === true || d.isPendingActionItem === false
    );
    const ids = publishedDocs
      .filter((d) => {
        if (!d.requiresAcceptance || d.myAcknowledgementStatus !== MY_ACK_STATUS.Pending) return false;
        if (hasExplicitPendingActionFlag) return d.isPendingActionItem === true;
        return true;
      })
      .map((d) => String(d.acknowledgementId || "").trim())
      .filter(Boolean);
    return new Set(ids);
  }, [publishedDocs]);

  /** Browse tab lists reference docs and acknowledged items only; pending acknowledgements stay on Required Actions. */
  const isPendingAcknowledgementItem = (d) =>
    d.requiresAcceptance && d.myAcknowledgementStatus === MY_ACK_STATUS.Pending;

  const publishedDocsForBrowseTab = useMemo(
    () => publishedDocs.filter((d) => !isPendingAcknowledgementItem(d)),
    [publishedDocs]
  );

  useEffect(() => {
    if (!publishedAckIdsToHideFromRequired.size) return;
    setDocuments((prev) => {
      const next = prev.filter(
        (d) => !publishedAckIdsToHideFromRequired.has(String(d.acknowledgementId || "").trim())
      );
      if (next.length === prev.length) return prev;
      const removed = prev.length - next.length;
      setRequiredTotal((t) => (typeof t === "number" ? Math.max(0, t - removed) : t));
      return next;
    });
  }, [publishedAckIdsToHideFromRequired]);

  useEffect(() => {
    if (!showPublishedCompanyTab) return;
    const loadOffices = async () => {
      try {
        const { Office } = config.REST_API;
        const offRes = await axiosBasic.get(Office.GetCompanyOffices);
        const offBody = offRes.data || {};
        if (offBody.success && Array.isArray(offBody.data)) {
          setOffices(
            offBody.data.map((o) => ({
              id: o.id,
              label: o.countryName ? `${o.name} (${o.countryName})` : o.name,
            }))
          );
        } else {
          setOffices([]);
        }
      } catch {
        setOffices([]);
      }
    };
    loadOffices();
  }, [showPublishedCompanyTab]);

  useEffect(() => {
    loadRequired(false);
  }, [loadRequired]);

  useEffect(() => {
    if (!showPublishedCompanyTab) {
      setPublishedDocs([]);
      setPublishedTotal(null);
      setPortalAcceptanceStats(null);
      publishedPageRef.current = 1;
      setPublishedLoading(false);
      setPublishedLoadingMore(false);
      return;
    }
    loadPublished(false);
  }, [loadPublished, showPublishedCompanyTab]);

  useEffect(() => {
    if (!selectedDoc) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = buildBlobUrlFromBase64(selectedDoc.fileContentBase64, selectedDoc.contentType);
    setPreviewUrl(url);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [selectedDoc]);

  const allowRejection = selectedDoc?.allowRejection !== false;
  const commentWithinLimit = comment.length <= ACK_COMMENT_MAX_LENGTH;
  const canSubmit =
    commentWithinLimit &&
    (response === "accept" || (allowRejection && response === "reject"));

  const closeModal = () => {
    setSelectedDoc(null);
    setResponse("");
    setComment("");
  };

  const handleSubmitResponse = async () => {
    if (!canSubmit || !selectedDoc) return;
    const documentId = String(selectedDoc.id ?? "").trim();
    if (!documentId) {
      Common.showErrorMessage("Missing document reference. Please refresh the page.");
      return;
    }
    setSubmitting(true);
    try {
      const { CompanyDocumentAcknowledgements } = config.REST_API;
      const status =
        response === "accept" ? API_ACCEPTANCE_STATUS.Accepted : API_ACCEPTANCE_STATUS.Rejected;
      const safeComment = comment.trim().slice(0, ACK_COMMENT_MAX_LENGTH);
      /** Backend: CompanyDocumentResponseRequest — acknowledgement resolved server-side by document + user */
      const res = await axiosBasic.post(
        CompanyDocumentAcknowledgements.Respond,
        { documentId, status, comment: safeComment || "" },
        { params: { page: 1, pageSize: PAGE_SIZE } }
      );
      const body = res.data || {};
      if (!body.success) {
        Common.showErrorMessage(body.message || "Could not submit your response.");
        return;
      }
      const respondStats = extractAcceptanceStatsFromPortalBody(body);
      if (respondStats) {
        setPortalAcceptanceStats(respondStats);
      }
      const { mapped, total } = buildRequiredDocumentsFromAcceptanceResponse(body);
      setDocuments(mapped);
      requiredPageRef.current = 1;
      if (typeof total === "number" && !Number.isNaN(total)) {
        setRequiredTotal(total);
      } else {
        setRequiredTotal(mapped.length);
      }
      Common.showSuccessMessage("Your response has been recorded.");
      closeModal();
      if (showPublishedCompanyTab) {
        await loadPublished(false);
      }
    } catch (err) {
      Common.showErrorMessage(err?.response?.data?.message || err?.message || "Could not submit your response.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadPreview = () => {
    if (!selectedDoc || !previewUrl) return;
    const a = document.createElement("a");
    a.href = previewUrl;
    a.download = selectedDoc.fileName || "document";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const requiredCount = typeof requiredTotal === "number" ? requiredTotal : documents.length;
  const publishedCountDisplay =
    publishedLoading && publishedDocs.length === 0 ? "…" : publishedDocsForBrowseTab.length;
  const actionBadgeText = loading
    ? "…"
    : showPublishedCompanyTab
      ? `${requiredCount} Required · ${publishedCountDisplay} Published`
      : `${requiredCount} ${requiredCount === 1 ? "Action" : "Actions"}`;

  const showAckForm =
    selectedDoc &&
    (selectedDoc.source === "required" ||
      (selectedDoc.requiresAcceptance && selectedDoc.myAcknowledgementStatus === MY_ACK_STATUS.Pending));

  const modalStatusPill = () => {
    if (!selectedDoc) return { icon: "bi-hourglass-split", text: "Pending Action", tone: "pending" };
    if (!selectedDoc.requiresAcceptance) {
      return { icon: "bi-journal-text", text: "Reference", tone: "reference" };
    }
    if (selectedDoc.source === "required" || selectedDoc.myAcknowledgementStatus === MY_ACK_STATUS.Pending) {
      return { icon: "bi-hourglass-split", text: "Pending Action", tone: "pending" };
    }
    if (selectedDoc.myAcknowledgementStatus === MY_ACK_STATUS.Accepted) {
      return { icon: "bi-check-circle-fill", text: "Accepted", tone: "done" };
    }
    if (selectedDoc.myAcknowledgementStatus === MY_ACK_STATUS.Rejected) {
      return { icon: "bi-x-circle-fill", text: "Rejected", tone: "done" };
    }
    return { icon: "bi-eye", text: "View", tone: "neutral" };
  };

  const canLoadMoreRequired =
    typeof requiredTotal === "number" && documents.length > 0 && documents.length < requiredTotal;
  const canLoadMorePublished =
    typeof publishedTotal === "number" && publishedDocs.length > 0 && publishedDocs.length < publishedTotal;

  const publishedBrowseEmptyButPendingElsewhere =
    !publishedLoading && publishedDocs.length > 0 && publishedDocsForBrowseTab.length === 0;

  const ackModalPill = selectedDoc
    ? modalStatusPill()
    : { icon: "bi-hourglass-split", text: "Pending Action", tone: "pending" };

  const requiredActionsSegment = (
    <Segment className="documents-required-segment">
      <Dimmer.Dimmable
        dimmed={loading && !loadingMoreRequired}
        className="documents-required-dimmable documents-required-dimmable--portal"
      >
        <Dimmer active={loading && !loadingMoreRequired} inverted>
          <Loader>Loading</Loader>
        </Dimmer>
        {!loading && documents.length === 0 ? (
          <div className="documents-required-empty" role="status">
            <i className="bi bi-inbox documents-required-empty-icon" aria-hidden />
            <p className="documents-required-empty-title">Nothing pending</p>
            <p className="documents-required-empty-text">
              When administrators assign a document that needs your acknowledgement, it will appear here.
            </p>
          </div>
        ) : (
          <div className="documents-required-portal-section">
            <h2 className="documents-required-section-title">
              <i className="bi bi-exclamation-circle-fill documents-required-section-icon" aria-hidden />
              Required Actions
            </h2>
            <ul className="documents-required-portal-grid">
              {documents.map((doc) => {
                const urgent = isUrgentPriority(doc.priority);
                const typeLabel = formatFileTypeLabel(doc.fileType, doc.fileName);
                return (
                  <li key={`req-${doc.acknowledgementId || doc.id}`} className="documents-required-tile">
                    <div className="documents-required-tile-head">
                      <span
                        className={
                          urgent
                            ? "documents-required-badge documents-required-badge--urgent"
                            : "documents-required-badge documents-required-badge--pending"
                        }
                      >
                        {urgent ? "Urgent" : "Pending"}
                      </span>
                      <div className="documents-required-tile-cat-icon" title={getCategoryLabel(doc.category)}>
                        <i className={`bi ${categoryIconClass(doc.category)}`} aria-hidden />
                      </div>
                    </div>
                    <h3 className="documents-required-tile-title">{doc.title}</h3>
                    <p className="documents-required-tile-meta">
                      {typeLabel && typeLabel !== "—" ? `${typeLabel} Document` : "Document"} · Updated{" "}
                      {formatCreatedDate(doc.createdDate)}
                    </p>
                    <button
                      type="button"
                      className="btn btn-primary documents-btn documents-required-tile-btn"
                      onClick={() => {
                        setSelectedDoc(doc);
                        setResponse("");
                        setComment("");
                      }}
                    >
                      {tileActionLabel(doc)}
                    </button>
                  </li>
                );
              })}
            </ul>
            {documents.length > 0 ? (
              <div className="documents-load-more-wrap">
                <p className="documents-load-more-meta" role="status">
                  Showing {documents.length}
                  {typeof requiredTotal === "number" ? ` of ${requiredTotal}` : ""} required{" "}
                  {requiredTotal === 1 ? "item" : "items"}
                </p>
                {canLoadMoreRequired ? (
                  <button
                    type="button"
                    className="btn btn-outline-primary documents-load-more-btn"
                    disabled={loadingMoreRequired}
                    onClick={() => loadRequired(true)}
                  >
                    {loadingMoreRequired ? "Loading…" : "Load more"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </Dimmer.Dimmable>
    </Segment>
  );

  return (
    <div className="documents-page documents-required-page container-fluid px-3 px-md-4 py-4">
      <header className="documents-required-hero">
        <div className="documents-required-hero-content">
          <div className="documents-required-hero-left">
            <div className="documents-required-hero-icon" aria-hidden>
              <DocumentPortalCubeIcon />
            </div>
            <div className="documents-required-hero-text">
              <div className="documents-required-hero-title-row">
                <h1 className="documents-required-hero-title">Document Portal</h1>
                <span
                  className={`documents-required-hero-badge${showPublishedCompanyTab ? " documents-required-hero-badge--split" : ""
                    }`}
                >
                  {actionBadgeText}
                </span>
              </div>
              <p className="documents-required-hero-description">
                Manage your compliance documents, sign required policies, and access company resources.
              </p>
            </div>
          </div>
        </div>
      </header>

      {showPublishedCompanyTab ? (
        <div className="documents-portal-tabs-wrap">
          <div className="documents-portal-tablist" role="tablist" aria-label="Document portal sections">
            <button
              type="button"
              role="tab"
              id="documents-tab-published"
              aria-selected={portalTab === "published"}
              aria-controls="documents-panel-published"
              className={`documents-portal-tab${portalTab === "published" ? " is-active" : ""}`}
              onClick={() => setPortalTab("published")}
            >
              <i className="bi bi-collection documents-portal-tab-icon" aria-hidden />
              Published Documents ({publishedCountDisplay})
            </button>
            <button
              type="button"
              role="tab"
              id="documents-tab-required"
              aria-selected={portalTab === "required"}
              aria-controls="documents-panel-required"
              className={`documents-portal-tab${portalTab === "required" ? " is-active" : ""}`}
              onClick={() => setPortalTab("required")}
            >
              <i className="bi bi-exclamation-circle-fill documents-portal-tab-icon" aria-hidden />
              Required Actions ({requiredCount})
            </button>
          </div>

          {portalAcceptanceStats ? (
            <div
              className="documents-portal-acceptance-card"
              aria-label="Your acknowledgement summary for documents in scope"
            >
              <div className="documents-portal-acceptance-card-top">
                <div className="documents-portal-acceptance-card-label">
                  <i className="bi bi-graph-up-arrow documents-portal-acceptance-card-label-icon" aria-hidden />
                  Acknowledgement progress
                </div>
                <div
                  className="documents-portal-acceptance-pct"
                  title="Among documents you already accepted or rejected, the percentage that were accepted. Pending is excluded; 0% if you have no completed responses yet."
                >
                  {portalAcceptanceStats.acceptancePercent}%
                </div>
              </div>
              <AcceptanceStatusStackBar
                segments={portalAckBarSegments}
                trackClassName="documents-portal-acceptance-stack-track"
                ariaLabel={
                  portalAckBarSegments
                    ? `Status mix for ${portalAckBarSegments.denom} acknowledgement slot(s) in your scope: ${portalAckBarSegments.a} accepted, ${portalAckBarSegments.p} pending, ${portalAckBarSegments.r} rejected`
                    : undefined
                }
              />
              <div className="documents-portal-acceptance-counts" role="list">
                <span role="listitem">
                  <strong>{portalAcceptanceStats.acceptedCount}</strong> accepted
                </span>
                <span className="documents-portal-acceptance-counts-sep" aria-hidden>
                  ·
                </span>
                <span
                  role="listitem"
                  title="Documents still awaiting your accept or reject (matches your Required Actions queue total when unfiltered)"
                >
                  <strong>{portalAcceptanceStats.pendingCount}</strong> pending
                </span>
                <span className="documents-portal-acceptance-counts-sep" aria-hidden>
                  ·
                </span>
                <span role="listitem">
                  <strong>{portalAcceptanceStats.rejectedCount}</strong> rejected
                </span>
                <span className="documents-portal-acceptance-counts-sep" aria-hidden>
                  ·
                </span>
                <span role="listitem">
                  <strong>{portalAcceptanceStats.documentsNotRequiringAcceptance}</strong> reference (no ack)
                </span>
                {portalAcceptanceStats.totalRequiringAcceptance > 0 ? (
                  <>
                    <span className="documents-portal-acceptance-counts-sep documents-portal-acceptance-counts-sep--wide" aria-hidden>
                      ·
                    </span>
                    <span
                      role="listitem"
                      className="documents-portal-acceptance-counts-scope"
                      title="Total count of documents in your scope that require acknowledgement—equals accepted + pending + rejected above, not an extra category."
                    >
                      Total in scope: <strong>{portalAcceptanceStats.totalRequiringAcceptance}</strong>{" "}
                      {portalAcceptanceStats.totalRequiringAcceptance === 1 ? "document" : "documents"} require
                      acknowledgement
                    </span>
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {portalTab === "published" ? (
            <div
              id="documents-panel-published"
              role="tabpanel"
              aria-labelledby="documents-tab-published"
              className="documents-portal-panel"
            >
              <Segment className="documents-required-segment documents-published-segment">
                <Dimmer.Dimmable
                  dimmed={publishedLoading && !publishedLoadingMore}
                  className="documents-required-dimmable documents-required-dimmable--portal"
                >
                  <Dimmer active={publishedLoading && !publishedLoadingMore} inverted>
                    <Loader>Loading</Loader>
                  </Dimmer>
                  <div className="documents-published-toolbar">
                    <h2 className="documents-required-section-title documents-published-section-title">
                      <i className="bi bi-collection documents-required-section-icon" aria-hidden />
                      Published Company Documents
                    </h2>
                    <SelectFilter
                      value={filterOfficeId != null ? String(filterOfficeId) : ""}
                      onChange={(v) => setFilterOfficeId(v === "" ? null : Number(v))}
                      options={[{ value: "", label: "My office (default)" }, ...offices.map((o) => ({ value: String(o.id), label: o.label }))]}
                      ariaLabel="Filter by office"
                      wrapperClassName="documents-filter-office documents-published-office"
                      iconClassName="bi bi-building documents-filter-office-icon"
                      selectClassName="documents-input documents-filter-select"
                    />
                  </div>
                  {!publishedLoading && publishedDocs.length === 0 ? (
                    <div className="documents-required-empty documents-published-empty" role="status">
                      <i className="bi bi-journal-text documents-required-empty-icon" aria-hidden />
                      <p className="documents-required-empty-title">No Published Documents</p>
                      <p className="documents-required-empty-text">
                        Active documents published to your company for this scope will appear here.
                      </p>
                    </div>
                  ) : publishedBrowseEmptyButPendingElsewhere ? (
                    <div className="documents-required-empty documents-published-empty" role="status">
                      <i className="bi bi-inbox documents-required-empty-icon" aria-hidden />
                      <p className="documents-required-empty-title">Nothing to show here yet</p>
                      <p className="documents-required-empty-text">
                        Documents that need your acknowledgement are listed under the <strong>Required Actions</strong> tab.
                      </p>
                    </div>
                  ) : (
                    <div className="documents-published-body">
                      <ul className="documents-published-grid" aria-label="Published Company Documents">
                        {publishedDocsForBrowseTab.map((doc) => {
                          const typeLabel = formatFileTypeLabel(doc.fileType, doc.fileName);
                          const status = doc.myAcknowledgementStatus;
                          return (
                            <li key={`pub-${doc.acknowledgementId || doc.id}`} className="documents-published-card">
                              <div className="documents-published-card-head">
                                {!doc.requiresAcceptance ? (
                                  <span className="documents-published-status documents-published-status--info">Reference</span>
                                ) : (
                                  <span className={ackStatusChipClass(status)}>{ackStatusLabel(status)}</span>
                                )}
                                <div
                                  className="documents-published-card-icon"
                                  title={getCategoryLabel(doc.category)}
                                  aria-hidden
                                >
                                  <i className={`bi ${categoryIconClass(doc.category)}`} />
                                </div>
                              </div>
                              <h3 className="documents-published-card-title" title={doc.title}>
                                {doc.title}
                              </h3>
                              <p className="documents-published-card-meta">
                                <span className="documents-published-card-type">
                                  {typeLabel && typeLabel !== "—" ? typeLabel : "Document"}
                                </span>
                                <span className="documents-published-card-sep" aria-hidden>
                                  ·
                                </span>
                                <span>Updated {formatCreatedDate(doc.createdDate)}</span>
                                {doc.requiresAcceptance ? (
                                  <>
                                    <br />
                                    <span className="documents-published-card-responded">
                                      Responded {formatRespondedDateTime(doc.respondedDate)}
                                    </span>
                                  </>
                                ) : null}
                              </p>
                              <button
                                type="button"
                                className="documents-published-card-btn"
                                onClick={() => {
                                  setSelectedDoc(doc);
                                  setResponse("");
                                  setComment("");
                                }}
                              >
                                {tileActionLabel(doc)}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                      {publishedDocs.length > 0 ? (
                        <div className="documents-load-more-wrap">
                          <p className="documents-load-more-meta" role="status">
                            Showing {publishedDocsForBrowseTab.length}{" "}
                            {publishedDocsForBrowseTab.length === 1 ? "document" : "documents"} (Reference and Completed Acknowledgements)
                          </p>
                          {canLoadMorePublished ? (
                            <button
                              type="button"
                              className="btn btn-outline-primary documents-load-more-btn"
                              disabled={publishedLoadingMore}
                              onClick={() => loadPublished(true)}
                            >
                              {publishedLoadingMore ? "Loading…" : "Load more"}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  )}
                </Dimmer.Dimmable>
              </Segment>
            </div>
          ) : (
            <div
              id="documents-panel-required"
              role="tabpanel"
              aria-labelledby="documents-tab-required"
              className="documents-portal-panel"
            >
              {requiredActionsSegment}
            </div>
          )}
        </div>
      ) : (
        requiredActionsSegment
      )}

      {selectedDoc && (
        <div className="documents-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="required-ack-title">
          <div className="documents-modal">
            <div className="documents-modal-preview">
              <div className="documents-ack-top">
                <div
                  className={`documents-ack-pill documents-ack-pill--${ackModalPill.tone || "pending"}`}
                >
                  <i className={`bi ${ackModalPill.icon} documents-ack-pill-icon`} aria-hidden />
                  {ackModalPill.text}
                </div>
                <button type="button" className="documents-ack-close" onClick={closeModal} aria-label="Close acknowledgement dialog">
                  <i className="bi bi-x-lg" aria-hidden />
                </button>
              </div>

              <div className="documents-ack-document-title" id="required-ack-title">
                {selectedDoc.title}
              </div>
              <div className="documents-ack-document-meta">
                {getCategoryLabel(selectedDoc.category)}
                {selectedDoc.fileType ? ` · ${formatFileTypeLabel(selectedDoc.fileType, selectedDoc.fileName)}` : ""} ·{" "}
                {formatFileSize(selectedDoc.fileSize)}
                <br />
                <span className="documents-ack-meta-muted">
                  Applies to: {officeScopeLabel(selectedDoc)} · Added {formatCreatedDate(selectedDoc.createdDate)}
                </span>
              </div>

              <div className="documents-modal-sheet documents-ack-preview">
                {selectedDoc.requiresAcceptance ? (
                  <div className="documents-ack-preview-badge">
                    <i className="bi bi-shield-check" aria-hidden />
                    Acknowledgement Required
                  </div>
                ) : (
                  <div className="documents-ack-preview-badge documents-ack-preview-badge--muted">
                    <i className="bi bi-info-circle" aria-hidden />
                    No Acknowledgement Required
                  </div>
                )}

                {selectedDoc.filePreviewNote ? (
                  <p className="documents-ack-preview-text documents-ack-file-note" role="status">
                    {selectedDoc.filePreviewNote}
                  </p>
                ) : null}

                <div className="documents-required-ack-preview-wrap">
                  {previewUrl && isBrowserPreviewableMime(selectedDoc.contentType) ? (
                    <iframe
                      title="Document preview"
                      src={previewUrl}
                      className="documents-admin-preview-iframe documents-required-ack-iframe"
                    />
                  ) : previewUrl ? (
                    <div className="documents-admin-preview-fallback">
                      <p className="documents-upload-section-hint">
                        Inline preview is not available for this file type ({selectedDoc.contentType || "unknown"}).
                        You can download the file to open it.
                      </p>
                      <button type="button" className="btn btn-primary documents-btn" onClick={handleDownloadPreview}>
                        <i className="bi bi-download me-1" aria-hidden />
                        Download file
                      </button>
                    </div>
                  ) : selectedDoc.filePreviewNote ? null : (
                    <div className="documents-admin-preview-fallback">
                      <p className="documents-upload-section-hint">
                        No file content was returned for preview.
                      </p>
                    </div>
                  )}
                </div>

                <p className="documents-ack-preview-text documents-ack-preview-desc">
                  {selectedDoc.description
                    ? selectedDoc.description
                    : showAckForm
                      ? "Please review the document above (or download it), then submit your acknowledgement below."
                      : "Review the document above or download it for your records."}
                </p>
              </div>
            </div>
            {showAckForm ? (
              <div className="documents-modal-form documents-modal-form--ack">
                <div className="documents-ack-form documents-ack-form--panel">
                  <div className="documents-ack-form-header">
                    <div className="documents-ack-form-heading">Your response</div>
                    <div className="documents-card-subtitle">
                      {allowRejection
                        ? "Choose to accept or raise an objection. Comments are optional."
                        : "Confirm that you have read and accept this document."}
                    </div>
                  </div>

                  <div className="documents-ack-section-title">Select response</div>

                  <div
                    className={`documents-ack-response-row${allowRejection ? "" : " documents-ack-response-row--single"}`}
                    role="group"
                    aria-label="Acknowledgement response"
                  >
                    <button
                      type="button"
                      className={`btn documents-btn documents-ack-response-btn documents-ack-response-btn--accept${response === "accept" ? " is-selected" : ""
                        }`}
                      onClick={() => setResponse("accept")}
                    >
                      <i className="bi bi-check-circle-fill me-2" aria-hidden />
                      Accept
                    </button>
                    {allowRejection ? (
                      <button
                        type="button"
                        className={`btn documents-btn documents-ack-response-btn documents-ack-response-btn--reject${response === "reject" ? " is-selected" : ""
                          }`}
                        onClick={() => setResponse("reject")}
                      >
                        <i className="bi bi-x-circle-fill me-2" aria-hidden />
                        Raise objection
                      </button>
                    ) : null}
                  </div>

                  <div className="documents-ack-comment">
                    <div className="documents-ack-comment-label-row">
                      <label htmlFor="ack-comment" className="documents-stat-label documents-ack-comment-label">
                        Optional comments
                      </label>
                      <span
                        className={`documents-ack-comment-counter${comment.length >= ACK_COMMENT_MAX_LENGTH ? " documents-ack-comment-counter--limit" : ""
                          }`}
                        aria-live="polite"
                      >
                        {comment.length}/{ACK_COMMENT_MAX_LENGTH}
                      </span>
                    </div>
                    <textarea
                      id="ack-comment"
                      className={`documents-textarea documents-ack-textarea${comment.length >= ACK_COMMENT_MAX_LENGTH ? " is-at-limit" : ""}`}
                      value={comment}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v.length <= ACK_COMMENT_MAX_LENGTH) setComment(v);
                      }}
                      maxLength={ACK_COMMENT_MAX_LENGTH}
                      placeholder="Add any notes related to your selection (optional)…"
                      rows={4}
                    />
                    <p className="documents-ack-comment-hint">Maximum {ACK_COMMENT_MAX_LENGTH} characters.</p>
                  </div>

                  <div className="documents-modal-actions documents-ack-footer">
                    <button
                      className="btn btn-light documents-btn"
                      type="button"
                      disabled={submitting}
                      onClick={closeModal}
                    >
                      Close
                    </button>
                    <button
                      className="btn btn-primary documents-btn"
                      type="button"
                      disabled={!canSubmit || submitting}
                      onClick={handleSubmitResponse}
                    >
                      {submitting ? "Submitting…" : "Submit response"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="documents-modal-form documents-modal-form--readonly">
                <div className="documents-ack-form documents-ack-form-readonly">
                  <div className="documents-ack-form-header">
                    <div className="documents-ack-form-heading">
                      {selectedDoc.requiresAcceptance ? "Acknowledgement" : "Document"}
                    </div>
                    {!selectedDoc.requiresAcceptance ? (
                      <p className="documents-card-subtitle">This document does not require a response.</p>
                    ) : (
                      <>
                        <p className="documents-ack-readonly-status">
                          Status: <strong>{ackStatusLabel(selectedDoc.myAcknowledgementStatus)}</strong>
                        </p>
                        {selectedDoc.respondedDate ? (
                          <p className="documents-ack-readonly-date">
                            Responded: {formatRespondedDateTime(selectedDoc.respondedDate)}
                          </p>
                        ) : null}
                      </>
                    )}
                  </div>
                  <div className="documents-modal-actions documents-ack-footer">
                    <button className="btn btn-primary documents-btn" type="button" onClick={closeModal}>
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RequiredActions;
